/* variant-autoselect.js
   Purpose: On full page load, make sure the current (?variant=...) is reflected in the UI,
   then "bounce" once (neighbor option then back) to wake Buy buttons. Safe, non-invasive.
*/
(function () {
  const DEBUG = false; // flip to true to log

  function log(...args){ if (DEBUG) console.log('[variant-autoselect]', ...args); }

  function closestProductRoot(el) {
    return el.closest('.product, .product-section, .shopify-section') || document;
  }

  function getProductJSON(root) {
    try {
      const script =
        root.querySelector('script[type="application/json"][data-product]') ||
        root.querySelector('script[type="application/json"][data-product-json]') ||
        document.querySelector('script[type="application/json"][data-product]') ||
        document.querySelector('script[type="application/json"][data-product-json]');
      if (!script || !script.textContent) return null;
      return JSON.parse(script.textContent);
    } catch (e) {
      log('JSON parse error', e);
      return null;
    }
  }

  function currentVariantId(form) {
    const url = new URL(window.location.href);
    return (url.searchParams.get('variant') || form.querySelector('[name="id"]')?.value || '').trim();
  }

  function setSelectValue(sel, value) {
    if (!sel) return false;
    const opts = Array.from(sel.options || []);
    const match = opts.find(o => !o.disabled && String(o.value) === String(value)) ||
                  opts.find(o => !o.disabled && String(o.value).toLowerCase() === String(value).toLowerCase());
    if (!match) return false;
    if (sel.value !== match.value) {
      sel.value = match.value;
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // re-trigger handlers even if value already matches
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return true;
  }

  function clickIf(el) {
    if (!el || el.disabled) return false;
    el.click(); // lets theme listeners handle everything
    return true;
  }

  function optionControlsForIndex(root, idx) {
    return {
      // swatch buttons found by data-option-position/index
      swatches: Array.from(
        root.querySelectorAll(
          `[data-option-position="${idx + 1}"][data-option-value], [data-option-index="${idx + 1}"][data-option-value]`
        )
      ),
      // nth <select name="options[...]" >
      select: root.querySelectorAll('select[name^="options["]')[idx] || null,
      // radios: we’ll infer by DOM order if needed
      radiosByIndexName() {
        const all = Array.from(root.querySelectorAll('input[type="radio"][name^="options["]'));
        const names = Array.from(new Set(all.map(r => r.name)));
        const name = names[idx] || names[0];
        return all.filter(r => r.name === name);
      }
    };
  }

  function applyOptionValue(root, idx, value) {
    const group = optionControlsForIndex(root, idx);
    // try swatches
    if (group.swatches.length) {
      const target = group.swatches.find(b => {
        const v = b.getAttribute('data-option-value') || '';
        return v === String(value) || v.toLowerCase() === String(value).toLowerCase();
      });
      if (target) return clickIf(target);
    }
    // try radios
    const radios = group.radiosByIndexName();
    if (radios.length) {
      const target = radios.find(r => !r.disabled && (
        String(r.value) === String(value) ||
        String(r.value).toLowerCase() === String(value).toLowerCase()
      ));
      if (target) {
        if (!target.checked) target.checked = true;
        target.dispatchEvent(new Event('input', { bubbles: true }));
        target.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    // try select
    if (group.select) return setSelectValue(group.select, value);
    return false;
  }

  function nudgeCurrentSelection(root) {
    // retrigger selects
    root.querySelectorAll('select[name^="options["]').forEach(sel => {
      if (sel.value) {
        sel.dispatchEvent(new Event('input', { bubbles: true }));
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    // retrigger checked radios
    const byName = new Map();
    root.querySelectorAll('input[type="radio"][name^="options["]').forEach(r => {
      if (!byName.has(r.name)) byName.set(r.name, []);
      byName.get(r.name).push(r);
    });
    byName.forEach(radios => {
      const checked = radios.find(r => r.checked);
      if (checked) {
        checked.dispatchEvent(new Event('input', { bubbles: true }));
        checked.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    // retrigger active swatches
    const isActive = el =>
      el.getAttribute('aria-pressed') === 'true' ||
      el.classList.contains('is-selected') ||
      el.classList.contains('selected') ||
      el.classList.contains('active');
    const byPos = new Map();
    root.querySelectorAll('[data-option-value]').forEach(btn => {
      const pos = btn.getAttribute('data-option-position') || btn.getAttribute('data-option-index') || '0';
      if (!byPos.has(pos)) byPos.set(pos, []);
      byPos.get(pos).push(btn);
    });
    byPos.forEach(btns => {
      const active = btns.find(isActive);
      if (active && !active.disabled) active.click();
    });
  }

  function ensureButtonsEnabledIfAvailable(form, product, variantId) {
    const v = product?.variants?.find?.(vv => String(vv?.id) === String(variantId));
    if (!v?.available) return;
    const submit = form.querySelector('[type="submit"]');
    const sticky = (form.closest('.section') || document).querySelector('.sticky-cta .button');
    [submit, sticky].forEach(btn => {
      if (!btn) return;
      if (btn.hasAttribute('disabled')) btn.removeAttribute('disabled');
      if (btn.getAttribute('aria-disabled') === 'true') btn.setAttribute('aria-disabled', 'false');
      btn.classList.remove('disabled');
    });
  }

  // Core: reflect current variant in UI, then bounce
  function primeAndBounce(form) {
    const root = closestProductRoot(form);
    const product = getProductJSON(root);
    if (!product?.variants?.length) return true; // nothing to do, but stop retrying

    const vid = currentVariantId(form);
    const current = product.variants.find(v => String(v?.id) === String(vid)) ||
                    product.variants.find(v => v?.available) ||
                    product.variants[0];
    if (!current) return true;

    // 1) Apply current variant options to UI (quietly)
    (current.options || []).forEach((val, idx) => applyOptionValue(root, idx, val));
    nudgeCurrentSelection(root);

    // 2) Try to “bounce” Option 1: click a neighbor then back
    const opt1 = optionControlsForIndex(root, 0);
    let list = [];
    if (opt1.swatches.length) list = opt1.swatches;
    else if (opt1.select) list = Array.from(opt1.select.options).filter(o => !o.disabled).map(o => ({ _select: opt1.select, _value: o.value }));
    else {
      const radios = opt1.radiosByIndexName();
      list = radios;
    }

    if (list.length >= 2) {
      const clickFirst = () => {
        // back to the value matching current option1
        applyOptionValue(root, 0, current.options?.[0]);
        nudgeCurrentSelection(root);
        ensureButtonsEnabledIfAvailable(form, product, current.id);
      };

      // choose the "second" option 1 value
      const goSecond = () => {
        if (opt1.swatches.length) {
          clickIf(list[1]);
        } else if (opt1.select) {
          setSelectValue(opt1.select, list[1]._value);
        } else if (list[1]) {
          // radio
          const r = list[1];
          if (!r.disabled) {
            if (!r.checked) r.checked = true;
            r.dispatchEvent(new Event('input', { bubbles: true }));
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };

      // tiny delays so theme listeners settle
      setTimeout(() => {
        goSecond();
        setTimeout(clickFirst, 90);
      }, 90);

      return true; // success (scheduled)
    }

    // No neighbor — just re-fire and enable if available
    ensureButtonsEnabledIfAvailable(form, product, current.id);
    return true;
  }

  function controlsPresent(root) {
    return !!(
      root.querySelector('[data-option-value]') ||
      root.querySelector('select[name^="options["]') ||
      root.querySelector('input[type="radio"][name^="options["]')
    );
  }

  function runOnceForm(form) {
    const root = closestProductRoot(form);
    const started = Date.now();

    const tryRun = () => {
      // wait until controls exist AND the theme likely finished binding
      if (!controlsPresent(root)) {
        if (Date.now() - started < 5000) return setTimeout(tryRun, 200);
        log('controls never appeared — giving up');
        return;
      }
      primeAndBounce(form);
    };

    // after full load so everything’s mounted
    if (document.readyState === 'complete') {
      setTimeout(tryRun, 250);
    } else {
      window.addEventListener('load', () => setTimeout(tryRun, 250), { once: true });
    }
  }

  // Kick off for every product form on the page
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('product-form form').forEach(f => runOnceForm(f));
  });
})();
